/* eslint-disable max-lines */
import React, { MouseEvent, useMemo, useState } from 'react';
import { useSearch } from '@okapi/router-react';
import { WarningCircleIcon } from '@fronton/icons-react';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import { AxesValues, COLLECTIONS_STORE_NAME, selectCollectionInfo, VariantAxisPoint, VariantAxisProduct } from 'modules/collections';
import { RELATED_SERVICES_STORE_NAME, RelatedService, selectActiveServices } from 'modules/related-services';
import { ExperimentCookie } from 'types/common';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { Button } from '../../button';
import { CashbackPlate } from '../../price-view';
import { Typography } from '../../typography';
import { Select } from '../../inputs';
import { DrawerAxisPoints } from '../drawer-axis-points/drawer-axis-points';
import { ProductPrice } from '../product-price';
import { ProductServicesNew } from '../product-services-new';
import {
  productInfo,
  productInfoBasketBtnWrapper,
  productOnfoCashbackWrapper,
  productInfoDisclaimer,
  productInfoDisclaimerIcon,
  productInfoDisclaimerText,
  productInfoName,
  productInfoPriceContainer,
  productInfoVariants,
  productInfoVariantsContainer,
} from './product-info-styles';
import { AxisPoints } from '../axis-points-new';

type ProductInfoProps = {
  additionalInfo?: React.ReactNode;
  areAllSizesUnavailable?: boolean;
  basketHref: string;
  buttonLabel: string;
  currency?: string;
  disclaimer: string;
  onAddToCartClick: TFunction;
  onAxisPointClick?: (point: VariantAxisProduct) => void;
  onDisclaimerClick?: TFunction;
  onServiceClick: (service: RelatedService, position: number) => void;
  onServiceShowEvent?: (service: RelatedService, position: number) => void;
  pricePrefix?: string;
  serviceDisclaimer?: React.ReactNode;
  servicePrefixPrice?: string;
  basketHrefTarget?: '_blank' | '_self';
  withCredit?: boolean;
  landingName: string;
  experimentCookie?: ExperimentCookie;
};

export const ProductInfoNew: React.FC<ProductInfoProps> = ({
  additionalInfo,
  areAllSizesUnavailable,
  basketHref,
  buttonLabel,
  currency,
  disclaimer,
  onAxisPointClick,
  onAddToCartClick,
  onDisclaimerClick,
  onServiceClick,
  onServiceShowEvent,
  pricePrefix,
  serviceDisclaimer,
  servicePrefixPrice,
  basketHrefTarget,
  withCredit = false,
  landingName,
  experimentCookie,
}) => {
  const setSearchParam = useSearch()[1];
  const [showVariants, setShowVariants] = useState(false);
  const { collections, relatedServices } = useStoreon<State>(
    COLLECTIONS_STORE_NAME,
    RELATED_SERVICES_STORE_NAME
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeServices = useMemo(() => selectActiveServices(relatedServices), []);

  const [collectionInfo, setCollectionInfo] = useState(selectCollectionInfo(collections));

  const handleVariantClick = () => {
    setShowVariants(true);
  }

  const handleCloseVariants = () => {
    setShowVariants(false);
  }

  const handleAddToCartWheelClick = (e: MouseEvent) => {
    if (e.button === 1) {
      onAddToCartClick();
    }
  };

  const handlePointClick = (e: MouseEvent, point: VariantAxisPoint, axis: string) => {
    if (axis === '10002') {
      e.preventDefault();

      if (point.axisValue) {
        setSearchParam({ 'facade': point.axisValue });
      }

      const mediaPhoto = [...point.mediaPhoto];
      mediaPhoto[2] = point.axisMediaPhoto;

      const updatedColectionInfo = {
        ...collectionInfo,
        variants: {
          ...collectionInfo.variants,
          activePoint: {
            ...collectionInfo.variants.activePoint,
            axesValues: point.axesValues.map(v =>
              (v.id === axis)
                ? {
                  id: v.id,
                  axis: point.label,
                }
                : v
            )
          },
          variantsModel: collectionInfo.variants.variantsModel.map(variant => variant.axisMeta.attribute === '10001' ? ({
            ...variant,
            pointsOnAxis: variant.pointsOnAxis.map(axisPoint => {
              const productUrlParts = axisPoint.productUrl.split('?');
              productUrlParts[1] = point.productUrl;
              const productUrl = productUrlParts.join('');
              return {
                ...axisPoint,
                productUrl,
              }
            })
          }) : variant),
        }
      };

      if (onAxisPointClick) {
        onAxisPointClick({
          ...updatedColectionInfo.variants.activePoint,
          mediaPhoto,
        });
      }

      setCollectionInfo(updatedColectionInfo);
    }

    const elementName = collectionInfo.variants.variantsModel.map(({ axisMeta }) => axisMeta.name).join(' / ');
    const elementText = collectionInfo.variants.activePoint.axesValues.map(axisValue => axisValue.id === axis ? point.label : axisValue.axis).join(' / ');

    analyticsUtils.clickButtonEvent('productDetail / variants', elementText, elementName);
  }

  return (
    <div className={productInfo}>
      <Typography
        as="h1"
        className={productInfoName}
        color={areAllSizesUnavailable ? 'text-minor' : 'text-primary'}
        itemProp="name"
        size="subtitle"
        variant="l"
      >
        {collectionInfo.variants.activePoint.displayedName}
      </Typography>

      {collectionInfo.variants.activePoint.price.cashback && (
        <CashbackPlate
          cashback={collectionInfo.variants.activePoint.price.cashback}
          className={productOnfoCashbackWrapper}
          size="l"
        />
      )}

      {experimentCookie === 'B'
        ? (
          collectionInfo.variants.variantsModel.map(({ axisMeta, pointsOnAxis }, index) => (
            <AxisPoints
              activePoint={collectionInfo.variants.activePoint}
              axisMeta={axisMeta}
              axisPoints={pointsOnAxis}
              experimentCookie='B'
              landingName={landingName}
              key={createElementKey('axis-points', index)}
              onPointClick={handlePointClick}
            />
          )))
        : (
          <button className={productInfoVariantsContainer} onClick={handleVariantClick}>
            <Select
              className={productInfoVariants}
              disabled
              options={[{
                value: collectionInfo.variants.activePoint.productId,
                text: collectionInfo.variants.activePoint.axesValues.reduce((acc: string, value: AxesValues) => acc ? `${acc} / ${value.axis}` : value.axis, '')
              }]}
              value={collectionInfo.variants.activePoint.productId}
            />
          </button>
        )
      }

      {(areAllSizesUnavailable || !collectionInfo.variants.activePoint.available) && (
        <Typography variant="l" size="body_accent">
          Товар закончился
        </Typography>
      )}

      {!areAllSizesUnavailable && collectionInfo.variants.activePoint.available && (
        <>
          <div className={productInfoPriceContainer}>
            <ProductPrice
              currency={currency}
              discount={collectionInfo.variants.activePoint.price.discount}
              price={collectionInfo.variants.activePoint.price.price}
              pricePerQuantity={false}
              prevPrice={collectionInfo.variants.activePoint.price.prevPrice}
              prefix={pricePrefix}
              withCredit={withCredit}
            />
            <button className={productInfoDisclaimer} onClick={onDisclaimerClick}>
              <WarningCircleIcon className={productInfoDisclaimerIcon} type="outline" size="m" color="text-primary" />
              <Typography className={productInfoDisclaimerText} variant="s" size="body_long">
                {disclaimer}
              </Typography>
            </button>

            <div className={productInfoBasketBtnWrapper}>
              <Button
                href={basketHref}
                target={basketHrefTarget ?? '_self'}
                onClick={onAddToCartClick}
                onAuxClick={handleAddToCartWheelClick}
                variant="primary"
                wide>
                {buttonLabel}
              </Button>
            </div>
          </div>

          {activeServices && activeServices.length > 0 && (
            <ProductServicesNew
              activeServices={activeServices}
              onServiceClick={onServiceClick}
              onServiceShowEvent={onServiceShowEvent}
              prefixPrice={servicePrefixPrice}
              serviceDisclaimer={serviceDisclaimer}
            />
          )}

          {additionalInfo}
        </>
      )}

      <DrawerAxisPoints
        activePoint={collectionInfo.variants.activePoint}
        axisPoints={collectionInfo.variants.variantsModel}
        landingName={landingName}
        show={showVariants}
        onClose={handleCloseVariants}
      />
    </div>
  );
};
